import React, {createContext, FC, useContext, useMemo} from "react";
import {Outlet, useLocation, useNavigate, useParams} from "react-router-dom";
import {Alert, Segmented, Select, Spin, Tabs, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {useEventsListQuery} from "../../GraphQL/generated/graphql";
import {eventWorkspaceRoute} from "./eventWorkspaceRoutes";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {formatLocalDate} from "./adminDateFormat";
import "../../Styles/EventWorkspaceLayout.css";
import {shellBreadcrumbKeys, useShellBreadcrumbLabel} from "../../Contexts/ShellBreadcrumbContext";

export {eventScopedAdminRoute, eventWorkspaceRoute} from "./eventWorkspaceRoutes";

const {Text} = Typography;
export type SelectedEventWorkspaceEvent = {
    id: number;
    code: string;
    name: string;
    place?: string | null;
    description?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    arbitraryFieldsTemplateId?: number | null;
};

export type SelectedEventWorkspaceContextValue = {
    eventId: number;
    event: SelectedEventWorkspaceEvent;
};

type WorkspaceNavigationItem = {
    key: string;
    labelKey: string;
    defaultLabel: string;
    resolvePath?: (eventId: number) => string;
};

type WorkspaceNavigationGroup = {
    key: string;
    labelKey: string;
    defaultLabel: string;
    items: WorkspaceNavigationItem[];
};

const SelectedEventWorkspaceContext = createContext<SelectedEventWorkspaceContextValue | null>(null);

export const useSelectedEventWorkspace = () => {
    const context = useContext(SelectedEventWorkspaceContext);

    if (!context) {
        throw new Error("useSelectedEventWorkspace must be used inside EventWorkspaceLayout.");
    }

    return context;
};

// Two groups: daily work during the event, and setup that is done once before registration opens.
export const buildEventWorkspaceNavigation = (eventId: number): WorkspaceNavigationGroup[] => [
    {
        key: "operate",
        labelKey: "event-workspace.group-operate",
        defaultLabel: "Operate",
        items: [
            {
                key: "overview",
                labelKey: "event-workspace.overview",
                defaultLabel: "Overview",
                resolvePath: () => eventWorkspaceRoute(eventId, "overview")
            },
            {
                key: "participants",
                labelKey: "participants",
                defaultLabel: "Participants",
                resolvePath: () => eventWorkspaceRoute(eventId, "participants")
            },
            {
                key: "rooming",
                labelKey: "event-workspace.rooming",
                defaultLabel: "Reservations",
                resolvePath: () => eventWorkspaceRoute(eventId, "rooming")
            },
            {
                key: "payments",
                labelKey: "event-workspace.payments",
                defaultLabel: "Payments",
                resolvePath: () => eventWorkspaceRoute(eventId, "payments")
            },
            {
                key: "notifications",
                labelKey: "notifications",
                defaultLabel: "Notifications",
                resolvePath: () => eventWorkspaceRoute(eventId, "notifications")
            }
        ]
    },
    {
        key: "setup",
        labelKey: "event-workspace.group-setup",
        defaultLabel: "Set up",
        items: [
            {
                key: "settings",
                labelKey: "event-workspace.settings",
                defaultLabel: "Event basics",
                resolvePath: () => eventWorkspaceRoute(eventId, "settings")
            },
            {
                key: "registration-fees",
                labelKey: "event-workspace.registration-fees",
                defaultLabel: "Fees and payment",
                resolvePath: () => eventWorkspaceRoute(eventId, "registration-fees")
            },
            {
                key: "accommodation",
                labelKey: "event-workspace.accommodation",
                defaultLabel: "Accommodation",
                resolvePath: () => eventWorkspaceRoute(eventId, "accommodation")
            },
            {
                key: "activities",
                labelKey: "event-workspace.activities",
                defaultLabel: "Activities and add-ons",
                resolvePath: () => eventWorkspaceRoute(eventId, "activities")
            },
            {
                key: "registration-form",
                labelKey: "event-workspace.registration-form",
                defaultLabel: "Form",
                resolvePath: () => eventWorkspaceRoute(eventId, "registration-form")
            },
            {
                key: "certificate-template",
                labelKey: "event-workspace.certificate-template",
                defaultLabel: "Certificates",
                resolvePath: () => eventWorkspaceRoute(eventId, "certificate-template")
            },
            {
                key: "badge-template",
                labelKey: "event-workspace.badge-template",
                defaultLabel: "Accreditations",
                resolvePath: () => eventWorkspaceRoute(eventId, "badge-template")
            },
            {
                key: "audit",
                labelKey: "event-workspace.audit",
                defaultLabel: "Audit",
                resolvePath: () => eventWorkspaceRoute(eventId, "audit")
            }
        ]
    }
];

const parseRouteEventId = (value?: string) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const resolveActiveWorkspaceKey = (pathname: string, eventId: number) => {
    const prefix = `/Admin/Events/${eventId}/`;

    if (pathname.startsWith(prefix)) {
        const key = pathname.slice(prefix.length).split("/")[0];
        if (["overview", "settings", "registration-fees", "accommodation", "activities", "registration-form", "certificate-template", "badge-template", "participants", "check-in", "rooming", "payments", "notifications", "audit"].includes(key)) {
            return key;
        }
    }

    return "overview";
};

export const EventWorkspaceLayout: FC = () => {
    const {t} = useTranslation();
    const errorLabels = buildUserFacingErrorLabels(t);
    const navigate = useNavigate();
    const location = useLocation();
    const {eventId} = useParams();
    const numericEventId = parseRouteEventId(eventId);

    const eventQuery = useEventsListQuery({
        variables: {
            take: 1,
            where: numericEventId ? {id: {eq: numericEventId}} : undefined
        },
        skip: !numericEventId
    });

    const event = eventQuery.data?.events?.items?.[0] as SelectedEventWorkspaceEvent | undefined;
    useShellBreadcrumbLabel(numericEventId ? shellBreadcrumbKeys.event(numericEventId) : undefined, event?.name);
    const navigationGroups = useMemo(
        () => numericEventId ? buildEventWorkspaceNavigation(numericEventId) : [],
        [numericEventId]
    );
    const navigationEntries = useMemo(
        () => navigationGroups.flatMap(group => group.items),
        [navigationGroups]
    );
    const activeKey = numericEventId ? resolveActiveWorkspaceKey(location.pathname, numericEventId) : "overview";
    const activeGroup = navigationGroups.find(group => group.items.some(item => item.key === activeKey))
        ?? navigationGroups[0];
    const groupOptions = useMemo(
        () => navigationGroups.map(group => ({
            value: group.key,
            label: t(group.labelKey, {defaultValue: group.defaultLabel})
        })),
        [navigationGroups, t]
    );
    const sectionNavigationItems = useMemo(
        () => (activeGroup?.items ?? []).map(item => ({
            key: item.key,
            label: t(item.labelKey, {defaultValue: item.defaultLabel})
        })),
        [activeGroup, t]
    );
    const sectionNavigationOptions = useMemo(
        () => navigationGroups.map(group => ({
            label: t(group.labelKey, {defaultValue: group.defaultLabel}),
            options: group.items.map(item => ({
                value: item.key,
                label: t(item.labelKey, {defaultValue: item.defaultLabel})
            }))
        })),
        [navigationGroups, t]
    );
    const navigateToWorkspaceGroup = (groupKey: string) => {
        const firstItem = navigationGroups.find(group => group.key === groupKey)?.items[0];

        if (firstItem) {
            navigateToWorkspaceKey(firstItem.key);
        }
    };
    const navigateToWorkspaceKey = (key: string) => {
        const nextNavigation = navigationEntries.find(item => item.key === key);

        if (nextNavigation?.resolvePath && numericEventId) {
            navigate(nextNavigation.resolvePath(numericEventId));
        }
    };

    if (!numericEventId) {
        return (
            <div style={{padding: 24}}>
                <Alert
                    type="warning"
                    message={t("event", {defaultValue: "Event"})}
                    description={t("event-workspace.invalid-event", {defaultValue: "Invalid event"})}
                />
            </div>
        );
    }

    if (eventQuery.loading && !event) {
        return (
            <div style={{padding: 24}}>
                <Spin />
            </div>
        );
    }

    if (eventQuery.error) {
        return (
            <div style={{padding: 24}}>
                <Alert
                    type="error"
                    message={t("event-workspace.load-error", {defaultValue: "Event workspace unavailable"})}
                    description={getUserFacingErrorMessage(eventQuery.error, t, errorLabels)}
                />
            </div>
        );
    }

    if (!event) {
        return (
            <div style={{padding: 24}}>
                <Alert
                    type="error"
                    message={t("event", {defaultValue: "Event"})}
                    description={t("event-workspace.not-found", {defaultValue: "Event not found."})}
                />
            </div>
        );
    }

    const workspaceContext = {eventId: numericEventId, event};
    const eventPlace = event.place?.trim();
    const eventDateRange = `${formatLocalDate(event.startDate)} - ${formatLocalDate(event.endDate)}`;

    return (
        <SelectedEventWorkspaceContext.Provider value={workspaceContext}>
            <div className="event-workspace">
                <section className="event-workspace__header">
                    <div className="event-workspace__identity-row">
                        <div className="event-workspace__identity">
                            <Text className="event-workspace__eyebrow" strong>
                                {t("event-workspace.workspace", {defaultValue: "Event workspace"})}
                            </Text>
                            <div className="event-workspace__title-row">
                                <div className="event-workspace__title">
                                    <h1 className="event-workspace__event-name">{event.name}</h1>
                                    {eventPlace && (
                                        <Text className="event-workspace__place" type="secondary">
                                            {eventPlace}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="event-workspace__date-range">
                            <Text strong>{eventDateRange}</Text>
                        </div>
                    </div>
                    <nav
                        aria-label={t("event-workspace.navigation", {defaultValue: "Event workspace navigation"})}
                        className="event-workspace__navigation"
                    >
                        <Select
                            aria-label={t("event-workspace.section-selector", {defaultValue: "Event workspace section"})}
                            className="event-workspace__section-select"
                            options={sectionNavigationOptions}
                            value={activeKey}
                            onChange={navigateToWorkspaceKey}
                        />
                        <Segmented
                            aria-label={t("event-workspace.group-selector", {defaultValue: "Event workspace area"})}
                            className="event-workspace__group-switch"
                            options={groupOptions}
                            value={activeGroup?.key}
                            onChange={value => navigateToWorkspaceGroup(String(value))}
                        />
                        <Tabs
                            activeKey={activeKey}
                            className="event-workspace__tabs"
                            items={sectionNavigationItems}
                            onChange={navigateToWorkspaceKey}
                        />
                    </nav>
                </section>
                <div className="event-workspace__body">
                    <Outlet context={workspaceContext} />
                </div>
            </div>
        </SelectedEventWorkspaceContext.Provider>
    );
};

export default EventWorkspaceLayout;
